// Iben: Types to type all paths of an object
// See: https://stackoverflow.com/questions/58434389/typescript-deep-keyof-of-a-nested-object/58436959#58436959
type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]];

type Join<K, P> = K extends string | number
	? P extends string | number
		? `${K}${'' extends P ? '' : '.'}${P}`
		: never
	: never;

type Paths<T, D extends number = 10> = [D] extends [never]
	? never
	: T extends object
		? {
				[K in keyof T]-?: K extends string | number ? `${K}` | Join<K, Paths<T[K], Prev[D]>> : never;
			}[keyof T]
		: '';

const HomeTags = {
	Home: {
		Nav: {
			Home: 'home-nav-home',
			Offers: 'home-nav-offers',
			AboutUs: 'home-nav-about-us',
			Associations: 'home-nav-associations',
			MyAssociation: 'home-nav-my-association',
			Advice: 'home-nav-advice'
		},
		Header: {
			Search: {
				Field: 'home-search-field',
				ButtonSubmit: 'home-search-button-submit',
				ButtonClear: 'home-search-button-clear'
			},
			LinkGroup: {
				Group: 'home-header-link-group',
				Item: 'home-header-link-group-item',
				All: 'home-header-link-group-all'
			}
		}
	}
};

const Offers = {
	Sorting: {
		Select: 'offers-sorting-select'
	}
};

const Status = {
	Title: 'status-title',
	Description: 'status-description',
	Button: 'status-button'
};

const Redirects = {
	AuthenticationFailed: {
		Button: 'authentication-failed-button'
	}
};

const General = {
	Loading: 'general-loading',
	Header: {
		H1: 'general-header-h1',
		H2: 'general-header-h2',
		H3: 'general-header-h3',
		H4: 'general-header-h4',
		H5: 'general-header-h5',
		H6: 'general-header-h6'
	},
	Input: {
		Checkbox: 'general-checkbox',
		Error: 'general-input-error'
	},
	// libs/shared/ui/common/src/lib/components/tag/tag.component.html
	Tag: {
		Clear: 'general-tag-clear'
	},
	// libs/shared/ui/common/src/lib/components/alert/alert.component.html
	Alert: 'alert-component',
	// libs/shared/ui/common/src/lib/components/block/block.component.html
	Block: 'block-component',
	// libs/shared/ui/common/src/lib/components/link/link.component.html
	Link: 'link-component',
	// libs/shared/ui/common/src/lib/components/button/button.component.html
	Button: 'button-component',
	// libs/shared/ui/common/src/lib/components/fa-icon/fa-icon.component.html
	FaIcon: 'fa-icon-component',
	// libs/shared/ui/common/src/lib/components/image/image.component.html
	Image: 'image-component',
	// libs/shared/ui/common/src/lib/components/info/info.component.html
	Info: 'info-component',
	// libs/shared/ui/common/src/lib/components/modal/modal.component.html
	Modal: 'modal-component',
	// libs/shared/ui/common/src/lib/components/cjm-results/results.component.ts
	Results: 'results-component',
	// libs/shared/ui/breadcrumb/src/lib/ui/components/breadcrumb/breadcrumb.component.ts
	Breadcrumbs: {
		List: 'breadcrumb-list',
		Breadcrumb: 'breadcrumb'
	}
};

const Header = {
	Header: 'acm-header'
};

const Menu = {
	Link: 'menu-link'
};

const BigFooter = {
	Self: 'big-footer'
};

const GlobalFooter = {
	Self: 'global-footer'
};

const Footer = {
	Links: {
		CookieConsent: 'footer-links-cookie-consent'
	}
};

const Products = {
	// libs/v-loket/products/src/lib/ui/pages/products/products.component.html
	Overview: {
		Search: {
			Input: 'products-search-input',
			Clear: 'products-search-clear',
			Submit: 'products-search-submit'
		},
		Filtering: {
			SelectedTags: 'products-filtering-selected-tags'
		},
		Sorting: {
			Select: 'products-sorting-select'
		},
		Location: {
			Input: 'products-location-input'
		},
		Actions: {
			ClearAll: 'products-filter-bar-actions-clear-all'
		}
	},
	// libs/v-loket/products/src/lib/ui/pages/product-detail/product-detail.component.html
	Detail: {
		Heading: 'products-detail-heading',
		Sidebar: 'products-detail-sidebar',
		SidebarInfo: 'products-detail-sidebar-info',
		SidebarUserAction: 'products-detail-sidebar-user-action',
		MoreInfo: 'products-detail-more-info',
		Wysiwyg: 'products-detail-wysiwyg',
		ExtraCta: 'products-detail-extra-cta'
	}
};

// libs/shared/ui/toast/src/lib/ui/components/toaster/toaster.component.html
const Toaster = {
	Title: 'toaster-title',
	Subtitle: 'toaster-subtitle',
	Message: 'toaster-message',
	Link: 'toaster-link',
	Close: 'toaster-close'
};

// libs/shared/ui/modal/src/lib/ui/components/action-modal/action-modal.component.html
const ActionModal = {
	Actions: {
		Confirm: 'action-modal-actions-confirm',
		Cancel: 'action-modal-actions-cancel'
	}
};

// libs/v-loket/shared/src/lib/ui/components/filter-bar/filter-bar.component.html
const FilterBar = {
	UnrefinableFacets: {
		Item: 'filter-bar-unrefinable-facets-item'
	}
};

// libs/v-loket/shared/src/lib/ui/components/filter-selection-tags/filter-selection-tags.component.html
const FilterSelectionTags = {
	Item: 'filter-selection-tags-item'
};

// libs/v-loket/associations/src/lib/ui/pages/association-overview/association-overview-page.component.html
const Associations = {
	Search: {
		Input: 'associations-search-input',
		Clear: 'associations-search-clear',
		Submit: 'associations-search-submit'
	},
	Filtering: {
		SelectedTags: 'associations-filtering-selected-tags',
		TargetAudience: {
			Button: 'associations-filtering-target-audience-button'
		}
	},
	Sorting: {
		Select: 'associations-sorting-select'
	},
	Location: {
		Input: 'associations-location-input',
		Submit: 'associations-location-submit'
	},
	Actions: {
		ClearAll: 'associations-filter-bar-actions-clear-all'
	}
};

const AssociationDetail = {
	BecomeRepresentative: 'association-detail-become-representative'
};

// libs/v-loket/registration/src/lib/ui/pages/register/register.component.html
export const FVRegistration = {
	Actions: {
		Submit: 'fv-registration-actions-submit'
	}
};

// libs/v-loket/registration/src/lib/ui/components/basic-registration-data-form/basic-registration-data-form.component.html
export const BasicRegistrationDataForm = {
	AssociationName: {
		Input: 'basic-registration-data-form-association-name-input'
	}
};

// libs/v-loket/shared/src/lib/ui/components/location-form/location-form.component.html
export const LocationForm = {
	Type: {
		Input: 'location-form-type-input'
	},
	Description: {
		Input: 'location-form-description-input'
	},
	Zipcode: {
		Input: 'location-form-zipcode-input'
	},
	Locality: {
		Input: 'location-form-locality-input'
	},
	Street: {
		Input: 'location-form-street-input'
	},
	Number: {
		Input: 'location-form-number-input'
	},
	Box: {
		Input: 'location-form-box-input'
	},
	Country: {
		Input: 'location-form-country-input'
	}
};

// libs/shared/ui/forms/src/lib/ui/components/country-select/country-select.component.html
export const CountrySelect = {
	Select: {
		Input: 'location-form-select-input'
	},
	Other: {
		Input: 'location-form-other-input'
	}
};

// libs/v-loket/shared/src/lib/ui/components/representative-form/representative-form.component.html
export const RepresentativeForm = {
	Firstname: {
		Input: 'representative-firstname-input'
	},
	Surname: {
		Input: 'representative-surname-input'
	},
	Callname: {
		Input: 'representative-callname-input'
	},
	Type: {
		Input: 'representative-type-input'
	},
	Role: {
		Input: 'representative-role-input'
	},
	Email: {
		Input: 'representative-email-input'
	},
	Phone: {
		Input: 'representative-phone-input'
	},
	MobilePhone: {
		Input: 'representative-mobile-phone-input'
	},
	SocialMedia: {
		Input: 'representative-social-media-input'
	},
	RRN: {
		Input: 'representative-rrn-input'
	},
	InviteMessage: {
		Input: 'representative-invite-message-input'
	}
};

// libs/v-loket/shared/src/lib/ui/components/send-copy/send-copy.component.html
export const SendCopy = {
	IsActive: {
		Input: 'send-copy-is-active-input'
	},
	Recipient: {
		Input: 'send-copy-recipient-input'
	}
};

// libs/v-loket/my-association/shared/src/lib/ui/components/association-detail-info-list-card/detail-info-list-card.html
export const DetailInfoListCard = {
	Wrapper: 'detail-info-list-card',
	Address: 'detail-info-list-card-address',
	Website: 'detail-info-list-card-website',
	Phone: 'detail-info-list-card-phone',
	SocialMedia: 'detail-info-list-card-social-media',
	Email: 'detail-info-list-card-email',
	Actions: {
		Delete: 'detail-info-list-card-action-delete',
		Revoke: 'detail-info-list-card-action-revoke',
		Edit: 'detail-info-list-card-action-edit'
	}
};

// libs/v-loket/shared/src/lib/ui/components/target-audience-selector/target-audience-selector.component.html
export const TargetAudienceSelector = {
	Input: {
		MaxAge: 'target-audience-selector-input-max-age',
		MinAge: 'target-audience-selector-input-min-age',
		AllAges: 'target-audience-selector-input-all-ages'
	},
	Slider: {
		MinAge: 'target-audience-selector-slider-min',
		MaxAge: 'target-audience-selector-slider-max'
	}
};

// libs/v-loket/shared/src/lib/ui/components/association-target-audience-tags/association-target-audience-tags.component.html
export const TargetAudienceTags = {
	AgeNotSet: 'target-audience-tags-age-not-set',
	AgeNoRestriction: 'target-audience-tags-age-no-restriction',
	AgeMinMaxRestriction: 'target-audience-tags-age-min-max-restriction',
	AgeMinRestriction: 'target-audience-tags-age-min-restriction',
	AgeMaxRestriction: 'target-audience-tags-age-max-restriction'
};

// libs/v-loket/home/src/lib/ui/components/association-passport-section/association-passport-section.component.html
export const AssociationPassportSection = {
	RegistrationLink: 'association-passport-section-registration-link'
};

// libs/v-loket/home/src/lib/ui/components/in-the-spotlight-section/in-the-spotlight-section.component.html
export const InTheSpotlightSection = {
	AssociationCard: 'in-the-spotlight-section-association-card',
	ProductCard: 'in-the-spotlight-section-product-card'
};

// libs/v-loket/home/src/lib/ui/components/about-us-section/about-us-section.component.html
export const AboutUsSection = {
	Cards: 'about-us-section-cards',
	Card: 'about-us-section-card'
};

// libs/v-loket/shared/src/lib/ui/components/association-card/association-card.component.html
export const AssociationCard = {
	Link: 'association-card-link'
};

// libs/v-loket/shared/src/lib/ui/components/product-grid-item/product-grid-item.component.html
export const ProductGridItem = {
	Link: 'product-grid-item-link'
};

// libs/v-loket/shared/src/lib/ui/components/advice-card/advice-card.component.html
export const AdviceCard = {
	Link: 'advice-card-link'
};

// libs/v-loket/shared/src/lib/ui/components/tile-block/tile-block.component.html
export const TileBlock = {
	Self: 'tile-block'
};

// libs/v-loket/my-association/shared/src/lib/ui/components/association-data-banner/association-data-banner.component.html
export const MyAssociationAssociationDataBanner = {
	Fields: {
		Name: 'my-association-data-banner-name',
		Description: 'my-association-data-banner-description',
		StartDate: 'my-association-data-banner-start-date',
		MainActivityTag: 'my-association-data-banner-main-activity-tag'
	},
	Actions: {
		Edit: 'my-association-data-banner-edit'
	}
};

// libs/v-loket/my-association/shared/src/lib/ui/components/association-data-banner/association-data-banner.component.html
export const MyAssociationAssociationData = {
	Actions: {
		// Match DetailInfoSection.Contact
		AddContact: 'my-association-data-add-contact',
		// Match DetailInfoSection.Location
		AddLocation: 'my-association-data-add-location'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/components/basic-association-data-form/basic-association-data-form.component.html
export const MyAssociationAssociationDataForm = {
	Name: {
		Input: 'my-association-association-data-form-name-input'
	},
	Shortname: {
		Input: 'my-association-association-data-form-shortname-input'
	},
	Description: {
		Input: 'my-association-association-data-form-description-input'
	},
	StartDate: {
		Input: 'my-association-association-data-form-start-date-input'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/pages/association-data-edit/association-data-edit.component.html
export const MyAssociationAssociationDataEdit = {
	Actions: {
		Save: 'my-association-association-data-edit-actions-save'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/components/contact-item-form/contact-item-form.component.html
export const MyAssociationAssociationContactItemForm = {
	Wrapper: 'my-association-association-contact-item-form-wrapper',
	Fields: {
		Type: {
			Input: 'my-association-association-contact-item-form-type-input'
		},
		Value: {
			Input: 'my-association-association-contact-item-form-value-input'
		}
	},
	Actions: {
		New: {
			Update: 'my-association-association-contact-item-form-actions-new-update',
			Delete: 'my-association-association-contact-item-form-actions-new-discard'
		},
		Existing: {
			Enable: 'my-association-association-contact-item-form-actions-existing-enable',
			Disable: 'my-association-association-contact-item-form-actions-existing-disable',
			Update: 'my-association-association-contact-item-form-actions-existing-update',
			Delete: 'my-association-association-contact-item-form-actions-existing-delete',
			Undo: 'my-association-association-contact-item-form-actions-existing-undo'
		}
	}
};

// libs/v-loket/my-association/association/src/lib/ui/components/contact-description-form/contact-description-form.component.html
export const MyAssociationAssociationContactDescriptionForm = {
	Fields: {
		Description: {
			Input: 'my-association-association-contact-description-form-description-input'
		}
	},
	Actions: {
		Enable: 'my-association-association-contact-description-form-actions-existing-enable',
		Disable: 'my-association-association-contact-description-form-actions-existing-disable',
		Update: 'my-association-association-contact-description-form-actions-existing-update',
		Delete: 'my-association-association-contact-description-form-actions-existing-delete',
		Undo: 'my-association-association-contact-description-form-actions-existing-undo'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/pages/association-contact/association-contact.component.html
export const MyAssociationAssociationContactAdd = {
	Actions: {
		Cancel: 'my-association-association-contact-cancel'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/pages/association-contact-edit/association-contact-edit.component.html
export const MyAssociationAssociationContactEdit = {
	Actions: {
		Add: 'my-association-association-contact-edit-add',
		Cancel: 'my-association-association-contact-edit-cancel'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/pages/association-location/association-location.component.html
export const MyAssociationAssociationLocationAdd = {
	Actions: {
		Submit: 'my-association-association-location-add',
		Cancel: 'my-association-association-location-cancel'
	}
};

// libs/v-loket/my-association/association/src/lib/ui/pages/association-location-edit/association-location-edit.component.html
export const MyAssociationAssociationLocationEdit = {
	Actions: {
		Submit: 'my-association-association-location-edit',
		Cancel: 'my-association-association-location-cancel'
	}
};

// libs/v-loket/my-association/representatives/src/lib/ui/pages/representatives/representatives.component.html
export const MyAssociationRepresentatives = {
	Actions: {
		Add: 'my-association-representatives-actions-add',
		Save: 'my-association-representatives-actions-save',
		Cancel: 'my-association-representatives-actions-cancel'
	}
};

// libs/v-loket/my-association/representatives/src/lib/ui/pages/representative-invite/representative-invite.component.html
export const MyAssociationRepresentativesInvite = {
	Actions: {
		Invite: 'my-association-representatives-invite-actions-invite',
		Cancel: 'my-association-representatives-invite-actions-cancel'
	}
};

export const CypressTags = {
	...HomeTags,
	Header,
	Menu,
	BigFooter,
	GlobalFooter,
	Footer,
	Offers,
	Status,
	Redirects,
	General,
	Products,
	Toaster,
	ActionModal,
	FilterBar,
	FilterSelectionTags,
	Associations,
	AssociationDetail,
	FVRegistration,
	BasicRegistrationDataForm,
	LocationForm,
	CountrySelect,
	RepresentativeForm,
	SendCopy,
	DetailInfoListCard,
	TargetAudienceSelector,
	TargetAudienceTags,
	AssociationPassportSection,
	InTheSpotlightSection,
	AboutUsSection,
	AssociationCard,
	ProductGridItem,
	AdviceCard,
	TileBlock,
	MyAssociationAssociationDataBanner,
	MyAssociationAssociationData,
	MyAssociationAssociationDataForm,
	MyAssociationAssociationDataEdit,
	MyAssociationAssociationContactItemForm,
	MyAssociationAssociationContactDescriptionForm,
	MyAssociationAssociationContactAdd,
	MyAssociationAssociationContactEdit,
	MyAssociationAssociationLocationAdd,
	MyAssociationAssociationLocationEdit,
	MyAssociationRepresentatives,
	MyAssociationRepresentativesInvite
};
export type CypressTagsPaths = Paths<typeof CypressTags>;
